import { type FC, useMemo } from "react";
import { Toggle } from "@onshopfront/components";
import FontAwesomeSolid from "../../../Utilities/Platform/FontAwesome/FontAwesomeSolid";

export interface ColumnType {
    column: string;
    name: string;
    enabled: boolean;
}

export function hasColumnsValue(data: unknown): data is { columns: Array<ColumnType>; } {
    if(typeof data !== "object") {
        return false;
    }

    if(data === null) {
        return false;
    }

    if(!("columns" in data)) {
        return false;
    }

    return isColumnArrayType(data.columns);
}

export function isColumnArrayType(data: unknown): data is Array<ColumnType> {
    if(Array.isArray(data)) {
        for(const column of data) {
            if(!isColumnType(column)) {
                return false;
            }
        }
    }

    return true;
}

export function isColumnType(data: unknown): data is ColumnType {
    if(typeof data !== "object") {
        return false;
    }

    if(data === null) {
        return false;
    }

    return "column" in data && typeof data.column === "string" &&
        "name" in data && typeof data.name === "string" &&
        "enabled" in data && typeof data.enabled === "boolean";
}

interface Props {
    renameable: boolean;
    columns: Record<string, string>;
    value: Array<ColumnType>;
    onChange: (value: Array<ColumnType>) => void;
}

export const ReorderableTableSettings: FC<Props> = ({
    columns,
    value,
    renameable,
    onChange,
}) => {
    const options = useMemo<Array<ColumnType>>(() => {
        // Prepare the check to see if any of the columns are missing
        const notFound: Record<string, boolean> = {};

        for(const column in columns) {
            if(!Object.prototype.hasOwnProperty.call(columns, column)) {
                continue;
            }

            notFound[column] = true;
        }

        // Check the value for any invalid columns
        const results: Array<ColumnType> = [];

        for(const column of value) {
            if(!columns[column.column]) {
                continue;
            }

            notFound[column.column] = false;
            results.push(column);
        }

        // Add the missing columns
        for(const column in notFound) {
            if(!Object.prototype.hasOwnProperty.call(notFound, column)) {
                continue;
            }

            if(!notFound[column]) {
                continue;
            }

            results.push({
                column,
                name   : columns[column],
                enabled: false,
            });
        }

        return results;
    }, [ columns, value ]);

    const swapIndex = (from: number, to: number): void => {
        const columnCount = options.length;

        if(from < 0 || from >= columnCount || to < 0 || to >= columnCount) {
            return;
        }

        const columns = [ ...options ];
        const [ removed ] = columns.splice(from, 1);

        columns.splice(to, 0, removed);

        onChange(columns);
    };

    return (
        <table>
            <thead>
                <tr>
                    <th className="border-none" />
                    <th className="border-none">Name</th>
                    <th className="border-none">Enabled</th>
                </tr>
            </thead>
            <tbody>
                {options.map((option, index) => (
                    <tr key={option.column}>
                        <td className="border-none grid grid-rows-2 gap-1">
                            <button
                                type="button"
                                className={
                                    "cursor-pointer px-1 py-0 rounded-sm bg-neutral-200 hover:bg-neutral-300 " +
                                    "disabled:hover:bg-neutral-200 disabled:cursor-not-allowed"
                                }
                                disabled={index === 0}
                                onClick={() => {
                                    swapIndex(index, index - 1);
                                }}
                            >
                                <FontAwesomeSolid icon="caret-up" />
                            </button>
                            <button
                                type="button"
                                className={
                                    "cursor-pointer px-1 py-0 rounded-sm bg-neutral-200 hover:bg-neutral-300 " +
                                    "disabled:hover:bg-neutral-200 disabled:cursor-not-allowed"
                                }
                                disabled={index === options.length - 1}
                                onClick={() => {
                                    swapIndex(index, index + 1);
                                }}
                            >
                                <FontAwesomeSolid icon="caret-down" />
                            </button>
                        </td>
                        <td className="border-none">
                            {renameable ? (
                                <input
                                    type="name"
                                    className="p-2"
                                    value={option.name}
                                    placeholder={columns[option.column] ?? ""}
                                    onChange={e => {
                                        onChange([
                                            ...options.slice(0, index),
                                            {
                                                ...options[index],
                                                name: e.currentTarget.value,
                                            },
                                            ...options.slice(index + 1),
                                        ]);
                                    }}
                                />
                            ) : (
                                option.name
                            )}
                        </td>
                        <td className="border-none">
                            <Toggle
                                checked={option.enabled}
                                onChange={e => {
                                    onChange([
                                        ...options.slice(0, index),
                                        {
                                            ...options[index],
                                            enabled: e.currentTarget.checked,
                                        },
                                        ...options.slice(index + 1),
                                    ]);
                                }}
                            />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
